import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Newspaper, Clock, Heart, Share2, ChevronDown, ChevronUp, Radio } from 'lucide-react';
import { api } from '../services/api';
import { toast } from 'sonner';
import { useTheme } from './ThemeToggle';

export default function NewsFeed({ customerId }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [likedNews, setLikedNews] = useState([]);
  const { isDarkMode } = useTheme();

  const loadNews = async () => {
    try {
      const data = await api.getNews();
      setNews(data.news || []);
    } catch (error) {
      console.error('Erreur chargement actualités:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNews();
  }, []);

  const handleLike = async (newsId) => {
    if (likedNews.includes(newsId)) {
      setLikedNews(likedNews.filter(id => id !== newsId));
      return;
    }
    
    try {
      await api.likeNews(newsId, customerId);
      setLikedNews([...likedNews, newsId]);
      setNews(news.map(n => 
        n.id === newsId ? { ...n, likes: (n.likes || 0) + 1 } : n
      ));
    } catch (error) {
      console.error('Erreur like:', error);
    }
  };

  const handleShare = async (newsItem) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: newsItem.title,
          text: newsItem.content,
          url: window.location.href
        });
      } catch (error) {
        console.log('Partage annulé');
      }
    } else {
      navigator.clipboard.writeText(`${newsItem.title} - ${window.location.href}`);
      toast.success('Lien copié !');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
    if (diff < 172800000) return 'Hier';
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <motion.div 
          className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center"
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Radio className="w-5 h-5 text-emerald-400" />
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #064e3b 100%)',
        boxShadow: '0 0 40px rgba(16, 185, 129, 0.2), 0 15px 30px rgba(0,0,0,0.4)',
        border: '1px solid rgba(16, 185, 129, 0.3)'
      }}
    >
      {/* Étoiles animées */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-emerald-400"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.3 + 0.1
            }}
            animate={{ opacity: [0.1, 0.5, 0.1], scale: [1, 1.3, 1] }}
            transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
          />
        ))}
      </div>

      {/* Bordure supérieure */}
      <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />
      
      {/* HUD Corners */}
      <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-emerald-500/50" />
      <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-emerald-500/50" />
      <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-emerald-500/30" />
      <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-emerald-500/30" />

      {/* Header cliquable */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="relative w-full p-4 flex items-center justify-between transition-colors hover:bg-white/5"
      >
        <div className="flex items-center gap-3">
          <motion.div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}
            animate={{ boxShadow: ['0 0 10px rgba(16, 185, 129, 0.2)', '0 0 20px rgba(16, 185, 129, 0.4)', '0 0 10px rgba(16, 185, 129, 0.2)'] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Newspaper className="w-5 h-5 text-emerald-400" />
          </motion.div>
          <div className="text-left">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              📡 Transmissions
              {news.length > 0 && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  {news.length} SIGNAL{news.length > 1 ? 'S' : ''}
                </span>
              )}
            </h3>
            <p className="text-[11px] text-emerald-500/70 font-mono tracking-wider">
              STATION LPE • FRÉQUENCE ACTIVE
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <motion.div
            className="w-2 h-2 rounded-full bg-emerald-500"
            animate={{ opacity: [1, 0.5, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          />
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-emerald-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-emerald-400" />
          )}
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              {news.length === 0 ? (
                <div className="py-8 text-center">
                  <motion.div
                    className="w-14 h-14 mx-auto mb-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Radio className="w-7 h-7 text-emerald-400/50" />
                  </motion.div>
                  <p className="text-gray-400 font-medium">Aucune transmission</p>
                  <p className="text-xs text-gray-600 mt-1">Fréquence silencieuse...</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                  {news.map((item, idx) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="p-4 rounded-xl border transition-all hover:border-emerald-500/40"
                      style={{
                        background: 'rgba(0,0,0,0.3)',
                        borderColor: 'rgba(16, 185, 129, 0.2)'
                      }}
                    >
                      <div className="flex items-start gap-3 mb-2">
                        <span className="text-2xl">{item.emoji || '📢'}</span>
                        <div className="flex-1">
                          <h4 className="font-semibold text-white">{item.title}</h4>
                          <div className="flex items-center gap-2 text-xs mt-1 text-gray-500">
                            <Clock className="w-3 h-3" />
                            {formatDate(item.createdAt)}
                            {item.category && (
                              <span className="bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded text-[10px]">
                                {item.category}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-gray-300 mb-3">{item.content}</p>

                      {item.imageUrl && (
                        <img src={item.imageUrl} alt={item.title} className="w-full h-32 object-cover rounded-lg mb-3 border border-emerald-500/20" />
                      )}

                      <div className="flex items-center gap-4 pt-2 border-t border-white/10">
                        <button
                          onClick={() => handleLike(item.id)}
                          className={`flex items-center gap-1 text-sm transition-colors ${
                            likedNews.includes(item.id) ? 'text-red-500' : 'text-gray-500 hover:text-red-400'
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${likedNews.includes(item.id) ? 'fill-current' : ''}`} />
                          {item.likes || 0}
                        </button>
                        <button
                          onClick={() => handleShare(item)}
                          className="flex items-center gap-1 text-sm text-gray-500 hover:text-emerald-400 transition-colors"
                        >
                          <Share2 className="w-4 h-4" />
                          Partager
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer HUD */}
      <div className="px-4 py-2 border-t border-emerald-500/10 flex items-center justify-between">
        <span className="text-[10px] font-mono text-gray-600">📡 NEWS FEED v2.0</span>
        <div className="flex items-center gap-1">
          <motion.div className="w-1.5 h-1.5 rounded-full bg-emerald-500" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity }} />
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/50" />
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-300/30" />
        </div>
      </div>
    </motion.div>
  );
}
